import type { Block, Document, Inline, Text } from '@contentful/rich-text-types';

type RichTextNode = Block | Inline | Text;

interface NodeSplit {
  head?: RichTextNode;
  tail?: RichTextNode;
  words: number;
}

const WORD = /\S+/g;
const ELLIPSIS = '…';

const countWords = (value: string): number => value.match(WORD)?.length ?? 0;

const hasWords = (node: RichTextNode): boolean =>
  node.nodeType === 'text'
    ? countWords(node.value) > 0
    : ((node as Block).content as RichTextNode[] | undefined)?.some(hasWords) ?? false;

const splitText = (node: Text, budget: number): NodeSplit => {
  const words = countWords(node.value);
  if (words <= budget) return { head: node, words };

  // End offset of the last word that still fits.
  const word = new RegExp(WORD.source, 'g');
  let end = 0;
  for (let i = 0; i < budget; i++) {
    const match = word.exec(node.value);
    if (match) end = match.index + match[0].length;
  }

  return {
    head: { ...node, value: node.value.slice(0, end) },
    tail: { ...node, value: node.value.slice(end).trimStart() },
    words: budget,
  };
};

/** Splits a node after `budget` words; the node on the cut keeps its type in both halves. */
const splitNode = (node: RichTextNode, budget: number): NodeSplit => {
  // Past the limit everything goes to the rest, wordless nodes too, so the ellipsis ends the preview.
  if (budget <= 0) return { tail: node, words: 0 };
  if (node.nodeType === 'text') return splitText(node, budget);
  // A childless block (a horizontal rule) has no words.
  if (!(node as Block).content?.length) return { head: node, words: 0 };

  const heads: RichTextNode[] = [];
  const tails: RichTextNode[] = [];
  let words = 0;

  for (const child of (node as Block).content as RichTextNode[]) {
    const split = splitNode(child, budget - words);
    words += split.words;
    if (split.head) heads.push(split.head);
    if (split.tail) tails.push(split.tail);
  }

  return {
    head: heads.length ? ({ ...node, content: heads } as RichTextNode) : undefined,
    tail: tails.length ? ({ ...node, content: tails } as RichTextNode) : undefined,
    words,
  };
};

/** Copy of `node` with an ellipsis after its last text, which holds the preview's last word. */
const appendEllipsis = (node: RichTextNode): RichTextNode => {
  if (node.nodeType === 'text') return { ...node, value: `${node.value.trimEnd()}${ELLIPSIS}` };

  const content = (node as Block).content as RichTextNode[];
  if (!content.length) return node;

  return { ...node, content: [...content.slice(0, -1), appendEllipsis(content[content.length - 1])] } as RichTextNode;
};

export interface RichTextPreview {
  /** The first `limit` words, ending in an ellipsis. */
  preview: Document;
  /** Everything after them — together with `preview` it is the whole document, each word once. */
  rest: Document;
}

/**
 * Cuts a rich-text document after `limit` words for a "Show more" preview; undefined when no words
 * follow the limit. Words are whitespace-separated, so Chinese, Japanese or Thai text is never cut.
 */
export const splitRichTextAtWords = (document: Document, limit: number): RichTextPreview | undefined => {
  const { head, tail } = splitNode(document, limit);
  if (!head || !tail || !hasWords(tail)) return undefined;

  return { preview: appendEllipsis(head) as Document, rest: tail as Document };
};
